import { useState, useRef, useEffect } from "react";
import { ScrollReveal } from "@/components/ScrollReveal";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  ArrowRight,
  Sparkles,
  Loader2,
  Layers,
  Cpu,
  GitBranch,
  Gauge,
  Clock,
  RotateCcw,
  HelpCircle,
  ShieldCheck,
  PenLine,
  Send,
  Check,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLocale } from "@/i18n/LocaleProvider";
import type { Blueprint } from "@/lib/blueprint";
import { storeLastBlueprint, clearLastBlueprint } from "@/lib/blueprint";
import { readLeadEmail, storeLeadEmail, isValidEmail, normalizePrompt } from "@/lib/leadEmail";
import { LeadConsent } from "@/components/LeadConsent";
import { trackConversion } from "@/lib/tracking";
import { trackAnalyticsConversion, getVisitorId, getSessionId } from "@/lib/analytics";
import { useBlueprintCooldownMinutes, useBlueprintMaxChars } from "@/hooks/usePublicContent";
import { useCardSpotlight } from "@/hooks/useCardSpotlight";


const EXAMPLES: { label: string; prompt: string }[] = [
  {
    label: "A booking system for my physio practice",
    prompt:
      "A booking system for my physiotherapy practice where patients can see each therapist's real-time availability, book or reschedule appointments online, and pick a treatment type. It should send automatic email and SMS reminders to cut no-shows, let staff manage their calendars and block off time, store basic patient notes and treatment history, and handle recurring appointment series for ongoing therapy plans.",
  },
  {
    label: "A multi-tenant CRM for real estate agents",
    prompt:
      "A multi-tenant CRM for real estate agencies where each agency has its own isolated workspace, team members and role-based permissions. Agents need to manage property listings, track leads through a customizable pipeline, log calls, emails and viewings, schedule follow-ups, and match buyers to properties. Include a shared dashboard with deal metrics and commission tracking, plus email integration so conversations are attached to the right contact.",
  },
  {
    label: "An AI assistant that drafts legal contracts",
    prompt:
      "An AI assistant that drafts legal contracts from a library of reviewed templates (NDAs, service agreements, employment contracts). Users answer a guided questionnaire, the assistant fills in clauses and flags risky or missing terms, and a lawyer can review, comment and approve before the document is finalized. Include version history, e-signature, secure document storage and an audit trail of who changed what and when.",
  },
  {
    label: "A client portal for my design agency",
    prompt:
      "A client portal for my design agency where each client logs in to see their active projects, milestones and timelines, review and approve design deliverables with inline comments, and track revision rounds. It should handle file sharing and asset downloads, show invoices and payment status, send notifications when something needs their attention, and give my team an admin view to manage all clients and projects in one place.",
  },
];

const DEFAULT_MAX = 1000;
const COOLDOWN_KEY = "devcore_blueprint_cooldown_until";


const readStoredCooldown = (): number => {
  try {
    const raw = localStorage.getItem(COOLDOWN_KEY);
    if (!raw) return 0;
    const ts = Number(raw);
    return Number.isFinite(ts) && ts > Date.now() ? ts : 0;
  } catch {
    return 0;
  }
};

const formatRemaining = (ms: number): string => {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const BlueprintGenerator = ({ redirectOnResult = false }: { redirectOnResult?: boolean }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { localize, lang } = useLocale();
  const { t } = useTranslation();
  const { data: cooldownMinutes = 10 } = useBlueprintCooldownMinutes();
  const { data: MAX = DEFAULT_MAX } = useBlueprintMaxChars();
  const { ref: sectionSpotlightRef, onMouseMove: onSectionSpotlightMove } = useCardSpotlight<HTMLElement>();
  const COOLDOWN_MS = cooldownMinutes * 60 * 1000; // mirrors the server window
  const exampleLabels = t("blueprint.examples", { returnObjects: true }) as string[];
  const examplePromptsRaw = t("blueprint.examplePrompts", { returnObjects: true });
  const examplePrompts = Array.isArray(examplePromptsRaw) ? (examplePromptsRaw as string[]) : [];
  const promptFor = (i: number) => examplePrompts[i] ?? EXAMPLES[i].prompt;
  const [idea, setIdea] = useState("");
  const [leadEmail, setLeadEmail] = useState<string>(() => readLeadEmail());
  const [leadConsent, setLeadConsent] = useState(false);
  const [showGate, setShowGate] = useState(false);
  const isExample = (text: string) => {
    const n = normalizePrompt(text);
    return [...examplePrompts, ...EXAMPLES.map((e) => e.prompt)].some(
      (p) => normalizePrompt(p.slice(0, MAX)) === n,
    );
  };
  const [usedIdea, setUsedIdea] = useState("");
  const [usedBlueprintId, setUsedBlueprintId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [blueprint, setBlueprint] = useState<Blueprint | null>(null);
  const [rejected, setRejected] = useState<string | null>(null);
  const [cooldownUntil, setCooldownUntil] = useState<number>(0);
  const [now, setNow] = useState<number>(() => Date.now());
  const [cooldownInfoOpen, setCooldownInfoOpen] = useState(false);
  const resultRef = useRef<HTMLDivElement | null>(null);
  const promptBoxRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Auto-grow the textarea with its content; the ScrollArea handles overflow.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [idea]);

  const remaining = Math.max(0, cooldownUntil - now);
  const onCooldown = remaining > 0;
  const cooldownPct = onCooldown
    ? Math.min(100, Math.max(0, ((COOLDOWN_MS - remaining) / COOLDOWN_MS) * 100))
    : 100;

  // Restore any persisted cooldown on mount.
  useEffect(() => {
    const stored = readStoredCooldown();
    if (stored) setCooldownUntil(stored);
  }, []);

  // Hydrate a blueprint that was generated on another page (e.g. the homepage
  // section) and passed in via navigation state.
  useEffect(() => {
    const incoming = location.state as
      | { blueprint?: Blueprint; idea?: string; blueprintId?: string; focusInput?: boolean }
      | null;
    if (incoming?.blueprint) {
      setBlueprint(incoming.blueprint);
      setUsedIdea(incoming.idea ?? "");
      setUsedBlueprintId(incoming.blueprintId ?? null);
      // Clear the navigation state so a refresh doesn't re-show stale data.
      navigate(location.pathname, { replace: true, state: null });
    } else if (incoming?.focusInput) {
      // Scroll to the prompt card itself (not the textarea inside Radix
      // ScrollArea) and account for the fixed header. Then focus without
      // letting focus-scroll fight the section scroll.
      const scrollToPromptBox = () => {
        const el = document.getElementById("blueprint");
        if (!el) return;
        const headerOffset = window.innerWidth >= 768 ? 88 : 72;
        const top = el.getBoundingClientRect().top + window.scrollY - headerOffset;
        window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
        window.setTimeout(() => textareaRef.current?.focus({ preventScroll: true }), 180);
      };

      requestAnimationFrame(scrollToPromptBox);
      window.setTimeout(scrollToPromptBox, 120);
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Persist the latest blueprint so any contact CTA on the page (even outside
  // this component) can attach it to the contact form.
  useEffect(() => {
    if (blueprint) storeLastBlueprint(blueprint, usedIdea, usedBlueprintId ?? undefined);
  }, [blueprint, usedIdea, usedBlueprintId]);

  // Tick once per second while a cooldown is active.
  useEffect(() => {
    if (!onCooldown) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [onCooldown]);

  // Persist + apply a cooldown coming from the server.
  const applyCooldown = (iso?: string) => {
    const ts = iso ? new Date(iso).getTime() : Date.now() + COOLDOWN_MS;
    const safe = Number.isFinite(ts) ? ts : Date.now() + COOLDOWN_MS;
    setCooldownUntil(safe);
    setNow(Date.now());
    try {
      localStorage.setItem(COOLDOWN_KEY, String(safe));
    } catch {
      /* ignore */
    }
  };


  // Scroll the generated result into view, accounting for the fixed header.
  useEffect(() => {
    if (!blueprint && !rejected) return;
    const el = resultRef.current;
    if (!el) return;
    const headerOffset = window.innerWidth >= 768 ? 80 : 64;
    requestAnimationFrame(() => {
      const top = el.getBoundingClientRect().top + window.scrollY - headerOffset - 16;
      window.scrollTo({ top, behavior: "smooth" });
    });
  }, [blueprint, rejected]);


  const generate = async (fromGate = false) => {
    if (onCooldown) {
      toast.error(t("blueprint.errCooldown", { time: formatRemaining(remaining) }));
      return;
    }
    const trimmed = idea.trim();
    if (trimmed.length < 8) {
      toast.error(t("blueprint.errTooShort"));
      return;
    }
    const needsEmail = !isExample(trimmed);
    if (needsEmail && !fromGate) {
      setShowGate(true);
      return;
    }
    if (needsEmail) {
      if (!isValidEmail(leadEmail)) {
        toast.error(t("blueprint.gate.errEmail"));
        return;
      }
      if (!leadConsent) {
        toast.error(t("leadConsent.required"));
        return;
      }
      storeLeadEmail(leadEmail);
    }
    setLoading(true);
    setBlueprint(null);
    setRejected(null);
    try {
      const { data, error } = await supabase.functions.invoke("generate-blueprint", {
        body: {
          idea: trimmed,
          lang,
          visitorId: getVisitorId(),
          sessionId: getSessionId(),
          ...(needsEmail ? { email: leadEmail.trim(), consent: leadConsent } : {}),
        },
      });
      if (error) {
        // Try to surface the function's JSON error message.
        let msg = t("blueprint.errGeneric");
        try {
          const ctx = await (error as { context?: Response }).context?.json();
          if (ctx?.error) msg = ctx.error;
          if (ctx?.emailRequired) setShowGate(true);
          // Server enforces the limit too — sync our cooldown to its value.
          if (ctx?.cooldownUntil) applyCooldown(ctx.cooldownUntil as string);
        } catch {
          /* ignore */
        }
        toast.error(msg);
        return;
      }
      if (data?.cooldownUntil) applyCooldown(data.cooldownUntil as string);
      if (data?.cooldown) {
        toast.error((data.error as string) || t("blueprint.errGeneric"));
        return;
      }
      if (data?.rejected) {
        setRejected(data.reason as string);
        return;
      }
      if (data?.blueprint) {
        setShowGate(false);
        const bp = data.blueprint as Blueprint;
        const bpId = (data.blueprintId as string | null) ?? null;
        trackConversion("blueprint_generated");
        trackAnalyticsConversion("blueprint_generated");
        if (redirectOnResult) {
          // Hand the result off to the dedicated /blueprint page.
          navigate(localize("/blueprint"), { state: { blueprint: bp, idea: trimmed, blueprintId: bpId, email: needsEmail ? leadEmail.trim() : undefined } });
          return;
        }
        setBlueprint(bp);
        setUsedIdea(trimmed);
        setUsedBlueprintId(bpId);
      } else {
        toast.error(t("blueprint.errGeneric"));
      }
    } catch {
      toast.error(t("blueprint.errUnknown"));
    } finally {
      setLoading(false);
    }
  };


  const goToContact = () =>
    navigate(localize("/contact"), { state: { blueprint, idea: usedIdea, blueprintId: usedBlueprintId, email: readLeadEmail() || undefined } });

  const reset = () => {
    setBlueprint(null);
    setRejected(null);
    setIdea("");
    clearLastBlueprint();
  };

  return (
    <section
      id="blueprint"
      ref={sectionSpotlightRef}
      onMouseMove={onSectionSpotlightMove}
      className="group/section relative py-16 sm:py-24 md:py-32 text-foreground border-y border-border/60 overflow-hidden"
    >
      {/* Frosted glass pane — blurs the aurora strands behind the section */}
      <div className="absolute inset-0 bg-card/30 backdrop-blur-2xl backdrop-saturate-150 pointer-events-none" />
      <div className="absolute inset-0 tech-grid opacity-[0.1] pointer-events-none" />
      <div
        className="pointer-events-none absolute inset-0 z-0 opacity-0 transition-opacity duration-500 group-hover/section:opacity-100"
        style={{
          background:
            "radial-gradient(600px circle at var(--mx) var(--my), hsl(var(--accent) / 0.1), transparent 70%)",
        }}
      />
      <div className="absolute -right-20 top-1/4 w-96 h-96 bg-accent/[0.07] blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute -left-20 bottom-1/4 w-96 h-96 bg-primary/[0.06] blur-[120px] rounded-full pointer-events-none" />

      <div className="container relative z-10">
        <div className="grid lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] gap-10 lg:gap-16 items-center">
          {/* Left: copy */}
          <ScrollReveal className="text-center lg:text-left max-w-2xl mx-auto lg:mx-0">
            <p className="mono-label mb-4 inline-flex items-center gap-2">
              <Sparkles size={13} className="text-accent" />
              {t("blueprint.monoLabel")}
            </p>
            <h2 className="text-[1.55rem] sm:text-3xl md:text-5xl font-bold leading-[1.08] tracking-tight">
              {t("blueprint.headingLine1")}{" "}
              <span className="gradient-text">{t("blueprint.headingLine2")}</span>
            </h2>
            <p className="mt-5 text-muted-foreground text-base md:text-lg leading-relaxed">
              {t("blueprint.subtitle")}
            </p>

            {/* Example chips (desktop, under copy) */}
            {!blueprint && !rejected && (
              <div className="hidden lg:block mt-12">
                <p className="mono-label mb-3">{t("blueprint.examplesLabel")}</p>
                <div className="flex flex-wrap gap-2">
                {EXAMPLES.map((ex, i) => (
                  <button
                    key={ex.label}
                    onClick={() => setIdea(promptFor(i).slice(0, MAX))}
                    disabled={loading}
                    className="font-mono text-xs px-3.5 py-2 rounded-full border border-border bg-background/40 text-muted-foreground hover:text-accent hover:border-accent/50 transition-colors disabled:opacity-50"
                  >
                    {exampleLabels[i] ?? ex.label}
                  </button>
                ))}
                </div>
              </div>
            )}
          </ScrollReveal>

          {/* Right: input */}
          <ScrollReveal delay={120} className="w-full max-w-2xl mx-auto lg:mx-0 lg:max-w-none">
            {/* 3-step flow above the prompt box */}
            {!blueprint && !rejected && (
              <ol className="mb-10 md:mb-12 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-2">
                {(t("blueprint.flowSteps", { returnObjects: true }) as { title: string; desc: string }[]).map(
                  (step, i) => {
                    const StepIcon = [PenLine, Cpu, Send][i] ?? PenLine;
                    return (
                      <li key={step.title} className="relative flex sm:flex-col items-start gap-3">
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-accent ring-1 ring-accent/20">
                            <StepIcon size={17} />
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-foreground leading-snug">
                            <span className="font-mono text-xs text-muted-foreground/40 mr-1.5 tabular-nums">

                              0{i + 1}
                            </span>
                            {step.title}
                          </p>
                          <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                            {step.desc}
                          </p>
                        </div>
                      </li>
                    );
                  }
                )}
              </ol>
            )}
            <div className="relative rounded-3xl">
              <div aria-hidden="true" className="ai-glow-halo" />
              <div
                ref={promptBoxRef}
                className="ai-glow-border group relative z-10 rounded-3xl border border-border bg-card p-5 md:p-7 shadow-2xl transition-colors duration-300 hover:border-accent/40"
              >
              <span className="absolute -top-3 -right-3 z-20 inline-flex items-center gap-1.5 rounded-full bg-green-700 px-3.5 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-white shadow-lg shadow-green-900/40 ring-1 ring-green-600/50 rotate-3">
                <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
                {t("blueprint.freeBadge")}
              </span>
              <div className="relative z-10">
                <ScrollArea className="h-44 md:h-48 -mr-2 pr-2">
                  <textarea
                    ref={textareaRef}
                    value={idea}
                    onChange={(e) => setIdea(e.target.value.slice(0, MAX))}
                    onKeyDown={(e) => {
                      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") generate();
                    }}
                    placeholder={t("blueprint.placeholder")}
                    disabled={loading}
                    className="w-full min-h-[160px] md:min-h-[176px] resize-none overflow-hidden bg-transparent text-foreground placeholder:text-muted-foreground/60 text-base md:text-lg leading-relaxed outline-none focus:outline-none focus-visible:outline-none disabled:opacity-60"
                  />
                </ScrollArea>

                <div className="flex items-center justify-between gap-4 mt-3 pt-3">
                  <span className="font-mono text-xs text-muted-foreground/50 shrink-0 flex flex-col leading-tight sm:block sm:leading-normal">
                    <span>{idea.length}</span>
                    <span>/{MAX}</span>
                  </span>
                  {!(showGate && !loading && !onCooldown) && (
                  <span className="hover-glow-accent">
                    <button
                      onClick={() => generate()}
                      disabled={loading || onCooldown}
                      className="inline-flex items-center gap-2.5 px-6 py-3 btn-clip bg-primary text-primary-foreground btn-label-contrast font-medium whitespace-nowrap transition-transform duration-200 active:scale-[0.97] disabled:opacity-70 disabled:cursor-not-allowed group"
                    >
                      {loading ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          {t("blueprint.drafting")}
                        </>
                      ) : onCooldown ? (
                        <>
                          <Clock size={18} />
                          {t("blueprint.availableIn", { time: formatRemaining(remaining) })}
                        </>
                      ) : (
                        <>
                          {t("blueprint.generate")}
                          <ArrowRight
                            size={18}
                            className="transition-transform duration-300 group-hover:translate-x-1"
                          />
                        </>
                      )}
                    </button>
                  </span>
                  )}
                </div>

                {showGate && !loading && !onCooldown && (
                  <div className="mt-4 text-left">
                    <p className="text-sm font-semibold text-foreground">{t("blueprint.gate.title")}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{t("blueprint.gate.body")}</p>
                    <form
                      className="mt-3 flex flex-col sm:flex-row gap-3"
                      onSubmit={(e) => {
                        e.preventDefault();
                        generate(true);
                      }}
                    >
                      <label htmlFor="blueprint-lead-email" className="sr-only">{t("blueprint.gate.label")}</label>
                      <input
                        id="blueprint-lead-email"
                        type="email"
                        required
                        autoComplete="email"
                        maxLength={255}
                        value={leadEmail}
                        onChange={(e) => setLeadEmail(e.target.value)}
                        placeholder={t("blueprint.gate.placeholder")}
                        className="flex-1 h-12 rounded-md border border-input bg-background px-3 text-base text-foreground placeholder:text-muted-foreground/60 outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      />
                      <button
                        type="submit"
                        disabled={!isValidEmail(leadEmail) || !leadConsent}
                        className="inline-flex h-12 items-center justify-center gap-2 px-5 btn-clip bg-primary text-primary-foreground btn-label-contrast font-medium whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {t("blueprint.gate.submit")}
                        <ArrowRight size={16} />
                      </button>
                    </form>
                    <LeadConsent
                      id="blueprint-lead-consent"
                      checked={leadConsent}
                      onCheckedChange={setLeadConsent}
                      className="mt-3"
                    />
                  </div>
                )}

                {/* Cooldown progress */}
                {onCooldown && (
                  <div className="mt-4 pt-4 border-t border-border/60">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <span className="font-mono text-[11px] tracking-[0.12em] text-muted-foreground/60 inline-flex items-center gap-2">
                        <Clock size={12} className="text-accent" />
                        {t("blueprint.cooldownLabel", { minutes: cooldownMinutes })}
                      </span>
                      <span className="inline-flex items-center gap-2">
                        <span className="font-mono text-xs text-accent tabular-nums">
                          {formatRemaining(remaining)}
                        </span>
                        <button
                          type="button"
                          onClick={() => setCooldownInfoOpen(true)}
                          aria-label={t("blueprint.cooldownInfoAria")}
                          className="inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground/60 transition-colors hover:text-accent hover:bg-accent/10 focus:outline-none focus-visible:ring-1 focus-visible:ring-accent active:scale-95"
                        >
                          <HelpCircle size={14} />
                        </button>
                      </span>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-border/60 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-primary to-accent transition-[width] duration-1000 ease-linear"
                        style={{ width: `${cooldownPct}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Cooldown info modal */}
                <Dialog open={cooldownInfoOpen} onOpenChange={setCooldownInfoOpen}>
                  <DialogContent className="max-w-md border-border bg-card/95 backdrop-blur-xl overflow-hidden">
                    <div className="absolute inset-0 tech-grid opacity-[0.08] pointer-events-none" />
                    <div className="absolute -right-16 -top-16 w-56 h-56 bg-accent/[0.1] blur-[90px] rounded-full pointer-events-none" />
                    <DialogHeader className="relative">
                      <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-accent/30 bg-accent/10 text-accent mb-3">
                        <ShieldCheck size={20} />
                      </span>
                      <p className="mono-label text-left">{t("blueprint.cooldownInfoEyebrow")}</p>
                      <DialogTitle className="text-left text-2xl font-bold tracking-tight">
                        {t("blueprint.cooldownInfoTitle")}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="relative space-y-4 text-sm md:text-base leading-relaxed text-muted-foreground">
                      <p>{t("blueprint.cooldownInfoBody", { minutes: cooldownMinutes })}</p>
                      <p>{t("blueprint.cooldownInfoBody2")}</p>
                    </div>
                    <DialogClose asChild>
                      <button
                        type="button"
                        className="relative mt-2 inline-flex items-center justify-center gap-2 px-6 py-3 btn-clip bg-primary text-primary-foreground btn-label-contrast font-medium transition-transform duration-200 active:scale-[0.97]"
                      >
                        {t("blueprint.cooldownInfoCta")}
                      </button>
                    </DialogClose>
                  </DialogContent>
                </Dialog>
              </div>
              </div>
            </div>

            {/* Example chips (mobile / tablet, under input) */}
            {!blueprint && !rejected && (
              <div className="lg:hidden mt-5 text-center">
                <p className="mono-label mb-3">{t("blueprint.examplesLabel")}</p>
                <div className="flex flex-wrap gap-2 justify-center">
                {EXAMPLES.map((ex, i) => (
                  <button
                    key={ex.label}
                    onClick={() => setIdea(promptFor(i).slice(0, MAX))}
                    disabled={loading}
                    className="font-mono text-xs px-3.5 py-2 rounded-full border border-border bg-background/40 text-muted-foreground hover:text-accent hover:border-accent/50 transition-colors disabled:opacity-50"
                  >
                    {exampleLabels[i] ?? ex.label}
                  </button>
                ))}
                </div>
              </div>
            )}
          </ScrollReveal>
        </div>

        {/* Loading skeleton */}
        {loading && (
          <div className="max-w-4xl mx-auto mt-10 text-center">
            <p className="font-mono text-xs tracking-[0.15em] text-muted-foreground/60 animate-pulse">
              {t("blueprint.loading")}
            </p>
          </div>
        )}

        {/* Rejection */}
        {rejected && !loading && (
          <div ref={resultRef} className="max-w-2xl mx-auto mt-8 rounded-2xl border border-border bg-background/60 backdrop-blur-xl p-6 text-center">
            <p className="text-muted-foreground">{rejected}</p>
            <button
              onClick={reset}
              className="mt-4 inline-flex items-center gap-2 font-mono text-xs text-accent hover:underline underline-offset-4"
            >
              <RotateCcw size={13} /> {t("blueprint.tryAnother")}
            </button>
          </div>
        )}

        {/* Result */}
        {blueprint && !loading && (
          <div ref={resultRef} className="max-w-5xl mx-auto mt-12 animate-fade-in">
            {/* Header card */}
            <div className="rounded-3xl border border-border bg-background/60 backdrop-blur-xl p-5 md:p-8 shadow-2xl">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div>
                  <p className="mono-label mb-2">// proposed_blueprint</p>
                  <h3 className="text-2xl md:text-3xl font-bold tracking-tight">
                    {blueprint.projectName}
                  </h3>
                  <p className="text-accent mt-1.5 text-base md:text-lg">
                    {blueprint.tagline}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  <span className="inline-flex items-center gap-1.5 font-mono text-xs px-3 py-1.5 rounded-full border border-border bg-card">
                    <Gauge size={13} className="text-accent" />
                    {blueprint.complexity}
                  </span>
                  <span className="inline-flex items-center gap-1.5 font-mono text-xs px-3 py-1.5 rounded-full border border-border bg-card">
                    <Clock size={13} className="text-accent" />
                    {blueprint.estimatedTimeline}
                  </span>
                </div>
              </div>
              <p className="text-muted-foreground mt-5 leading-relaxed">
                {blueprint.summary}
              </p>
              <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3 rounded-2xl border border-accent/30 bg-accent/5 p-4">
                <p className="flex-1 text-sm text-foreground/85">{t("blueprint.ctaPanel.points", { returnObjects: true, joinArrays: " · " }) as unknown as string}</p>
                <button
                  onClick={goToContact}
                  className="inline-flex justify-center items-center gap-2 px-5 py-3 btn-clip bg-primary text-primary-foreground btn-label-contrast font-medium whitespace-nowrap transition-transform duration-200 active:scale-[0.97] group"
                >
                  {t("blueprint.ctaPanel.quick")}
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </div>

            {/* Grid: features + stack */}
            <div className="grid md:grid-cols-2 gap-5 mt-5">
              {/* Core features */}
              <div className="rounded-3xl border border-border bg-background/60 backdrop-blur-xl p-5 md:p-7">
                <p className="mono-label mb-5 inline-flex items-center gap-2">
                  <Layers size={13} className="text-accent" />
                  // core_features
                </p>
                <ul className="space-y-4">
                  {blueprint.coreFeatures?.map((f, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="font-mono text-xs text-accent/70 mt-1 shrink-0">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <div>
                        <p className="font-medium text-foreground">{f.title}</p>
                        <p className="text-sm text-muted-foreground leading-relaxed mt-0.5">
                          {f.description}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Tech stack + phases */}
              <div className="space-y-5">
                <div className="rounded-3xl border border-border bg-background/60 backdrop-blur-xl p-5 md:p-7">
                  <p className="mono-label mb-5 inline-flex items-center gap-2">
                    <Cpu size={13} className="text-accent" />
                    // recommended_stack
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {blueprint.techStack?.map((t, i) => (
                      <span
                        key={i}
                        className="font-mono text-xs px-3 py-1.5 rounded-lg border border-border bg-card text-foreground"
                      >
                        <span className="text-muted-foreground/60">{t.category}:</span>{" "}
                        {t.choice}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="rounded-3xl border border-border bg-background/60 backdrop-blur-xl p-5 md:p-7">
                  <p className="mono-label mb-5 inline-flex items-center gap-2">
                    <GitBranch size={13} className="text-accent" />
                    // delivery_phases
                  </p>
                  <ol className="relative border-l border-border/70 ml-1.5 space-y-5">
                    {blueprint.phases?.map((p, i) => (
                      <li key={i} className="pl-5 relative">
                        <span className="absolute -left-[5px] top-1.5 w-2 h-2 rounded-full bg-accent" />
                        <div className="flex items-baseline justify-between gap-3">
                          <p className="font-medium text-foreground">{p.name}</p>
                          <span className="font-mono text-xs text-muted-foreground/60 shrink-0">
                            {p.duration}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed mt-0.5">
                          {p.focus}
                        </p>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>

            {/* CTA panel */}
            <div className="relative mt-8 rounded-3xl">
              <div aria-hidden="true" className="ai-glow-halo" />
              <div className="ai-glow-border relative z-10 rounded-3xl border border-accent/40 bg-card p-6 md:p-10 shadow-2xl text-center">
                <h3 className="text-xl md:text-3xl font-bold tracking-tight text-foreground max-w-2xl mx-auto">
                  {t("blueprint.ctaPanel.title")}
                </h3>
                <p className="mt-3 text-muted-foreground md:text-lg max-w-xl mx-auto">
                  {t("blueprint.ctaPanel.body")}
                </p>
                <ul className="mt-5 flex flex-wrap justify-center gap-x-6 gap-y-2">
                  {(t("blueprint.ctaPanel.points", { returnObjects: true }) as string[]).map((pt) => (
                    <li key={pt} className="inline-flex items-center gap-2 text-sm text-foreground/80">
                      <Check size={15} className="text-accent" /> {pt}
                    </li>
                  ))}
                </ul>
                <div className="mt-7 flex flex-col items-center gap-4">
                  <span className="hover-glow-accent w-full sm:w-auto">
                    <button
                      onClick={goToContact}
                      className="inline-flex w-full sm:w-auto justify-center items-center gap-2.5 px-9 py-4 btn-clip bg-primary text-primary-foreground btn-label-contrast text-lg font-semibold transition-transform duration-200 active:scale-[0.97] group"
                    >
                      {t("blueprint.makeReal")}
                      <ArrowRight size={20} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                  </span>
                  <button
                    onClick={reset}
                    className="inline-flex min-h-[44px] items-center justify-center gap-2 font-mono text-xs text-muted-foreground hover:text-accent transition-colors"
                  >
                    <RotateCcw size={13} /> {t("blueprint.startOver")}
                  </button>
                </div>
              </div>
            </div>

            <p className="text-center font-mono text-[11px] tracking-[0.12em] text-muted-foreground/40 mt-6">
              {t("blueprint.footnote")}
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default BlueprintGenerator;
